import {TypesSocial} from "@dp-instant/interfaces";
import {ISocial} from "@dp-instant/social";
import {AdResponse} from "@dp-instant/interfaces";
import {ComponentClassName, useEventListener, useInjection} from "@dp-instant/ui";
import {math} from "@game/coregame/helpers";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {GameModel} from "@game/coregame/model/GameModel";
import {showReward} from "@game/helpers";
import {TypesGame} from "@game/TypesGame";
import {Booster, BoosterType} from "@game/ui/components/Booster/Booster";
import {UIConstants} from "@game/ui/constants";
import cn from "classnames";
import {useEffect, useState} from "preact/hooks";
import {toFixedString} from "@game/ui/uiHelpers";
import "./Boosters.less";
import {GameEvents} from "@game/coregame/GameEvents";
import {SIOConstants} from "@game/constants";
import {isBoosterGated, getBoosterGatePrice, getBoosterMaxLevel, BoosterKind} from "../../../../packages/shared/src/pricing";
import {apiClient} from "@game/api/ApiClient";
import {CatalogItemDTO} from "../../../../packages/shared/src/index";
import {InsufficientCoinsPopup} from "@game/ui/popups/InsufficientCoinsPopup";

let isFirstOpenPerSession = true;

const isAdThrottledFor = (id: string): boolean => {
    if (__SOCIAL__ == "ya") {
        if (SIOConstants.REWARD_AD_PLAYED.has(id)) {
            if (SIOConstants.REWARD_AD_PLAYED.get(id)! > Date.now() - 60_000) {
                return true;
            }
        }
    }
    return false;
};
export const Boosters = ({className}: ComponentClassName) => {
    const boostersData = UIConstants.boosters;
    const model = useInjection<GameModel>(TypesGame.model);
    const social = useInjection<ISocial>(TypesSocial.model);

    useEventListener("YANDEX_SYNC", () => {
        setState(state => ({...state, coins: model.cookie.coins}));
    });

    const initialState = {
        coins: model.cookie.coins,
        startCoins: model.cookie.coins,
        boosters: boostersData,
        adViewed: false,
        tierVersion: 0,
        coinPacks: [] as CatalogItemDTO[],
        insufficientPopup: null as { deficit: number; pack: CatalogItemDTO } | null
    };
    const [state, setState] = useState(() => initialState);

    // Load coin packs catalog (TG only)
    useEffect(() => {
        if (__SOCIAL__ === "tg") {
            apiClient.getCatalog().then(items => {
                const packs = items.filter(i => i.active && i.type === "consumable" && (i.payload as any)?.coins);
                setState(s => ({...s, coinPacks: packs}));
            }).catch(() => {});
        }
    }, []);
    const {startCoins, coins, adViewed} = state;

    const updateBoostersState = () => {
        let boosters = updateBoosters();
        setState(state => ({...state, boosters: boosters}));

        if (isFirstOpenPerSession) {
            isFirstOpenPerSession = false;
        }
    };

    useEffect(updateBoostersState, [coins, adViewed, isFirstOpenPerSession, state.tierVersion]);
    useEventListener(GameEvents.COINS_UPDATED, updateBoostersState);

    const checkCoins = () => {
        setState(state => ({...state, coins: model.cookie.coins}));
    };
    const now = Date.now();
    const updateBoosters = () => {
        let isFirstTime = social.session.ftue && isFirstOpenPerSession;
        const canOfferAd = false;

        let boosters = [];
        for (let i = 0; i < boostersData.length; i++) {
            boosters[i] = {...boostersData[i], ...updateBoost(boostersData[i])};
        }

        let pricesByType: {id: string; price: number}[] = [];
        boosters.forEach(item => {
            pricesByType.push({id: item.id, price: item.price});
        });

        pricesByType.sort((a, b) => b.price - a.price);
        const mostExpensiveUpgradeType = pricesByType[0].id;

        boosters.forEach(item => {
            item.isFree = false;
            item.disabled = !model.cookie.isEnoughCoins(item.price);
            if (item.id === mostExpensiveUpgradeType && item.disabled) {
                if (canOfferAd) {
                    item.disabled = true;
                    item.isFree = false;
                }
            }
            if (isAdThrottledFor("boosters")) {
                item.disabled = true;
            }

            let increaseBoosterFreeFn = () => {};
            let increaseBoosterFn = () => {};

            switch (item.id) {
                case BoosterType.START_UNITS:
                    increaseBoosterFn = () => {
                        model.meta.increaseStartPopulation();
                    };
                    increaseBoosterFreeFn = () => {
                        model.meta.increaseStartPopulationFree();
                    };
                    break;
                case BoosterType.START_PRODUCE:
                    increaseBoosterFn = () => {
                        model.meta.increaseSpawn();
                    };
                    increaseBoosterFreeFn = () => {
                        model.meta.increaseSpawnFree();
                    };
                    break;
                case BoosterType.OFFLINE_EARNINGS:
                    increaseBoosterFn = () => {
                        model.meta.increaseOffline();
                    };
                    increaseBoosterFreeFn = () => {
                        model.meta.increaseOfflineFree();
                    };
            }

            item.onClick = async () => {
                setState(state => ({...state, adViewed: false}));
                if (item.isFree && canOfferAd) {
                    const result = await showReward(model.cookie.getVip);
                    if (result) {
                        SIOConstants.REWARD_AD_PLAYED.set("boosters", Date.now());
                        increaseBoosterFreeFn();
                        setState(state => ({...state, adViewed: true}));
                    }
                } else {
                    increaseBoosterFn();
                    checkCoins();
                }
            };

            // Insufficient coins handler (TG only)
            if (__SOCIAL__ === "tg" && item.disabled && !item.isGated) {
                item.onInsufficientCoins = async (boosterPrice: number) => {
                    const deficit = boosterPrice - model.cookie.coins;
                    let packs = state.coinPacks;
                    // Retry catalog load if packs are empty (iOS timing issue)
                    if (packs.length === 0) {
                        try {
                            const items = await apiClient.getCatalog();
                            packs = items.filter(i => i.active && i.type === "consumable" && (i.payload as any)?.coins);
                            setState(s => ({...s, coinPacks: packs}));
                        } catch {}
                    }
                    // Find cheapest pack that covers the deficit
                    const suitable = packs
                        .filter(p => ((p.payload as any)?.coins ?? 0) >= deficit)
                        .sort((a, b) => a.price - b.price);
                    const pack = suitable.length > 0
                        ? suitable[0]
                        : packs.length > 0
                            ? packs.reduce((best, p) => ((p.payload as any)?.coins ?? 0) > ((best.payload as any)?.coins ?? 0) ? p : best, packs[0])
                            : null;
                    if (pack) {
                        setState(s => ({...s, insufficientPopup: {deficit, pack}}));
                    }
                };
            }

            if (isFirstTime) {
                item.disabled = true;
                item.isFree = false;
            }
        });

        return boosters;
    };

    const boosterKindMap: Record<string, BoosterKind> = {
        [BoosterType.START_PRODUCE]: "spawn",
        [BoosterType.START_UNITS]: "startPop",
        [BoosterType.OFFLINE_EARNINGS]: "offline"
    };

    const updateBoost = (boost: any) => {
        let defaultCount: number | string = 11;
        let [price, levelNum, disabled] = [99, 66, false];
        let isFree = false;
        let isGated = false;
        let gatePrice = 0;
        let onClick = async () => {};
        let onUnlock = async () => {};
        switch (boost.id) {
            case BoosterType.START_UNITS:
                levelNum = model.cookie.playerStartPopulation;
                price = model.meta.getStartPopulationCost();
                defaultCount = model.meta.getStartPopulation(PlayerType.First);
                break;
            case BoosterType.START_PRODUCE:
                levelNum = model.cookie.playerSpawnLevel;
                price = model.meta.getPopulationRateCost();
                defaultCount = math.round(model.meta.getPlayerGenerationRateValue(levelNum), 100);
                break;
            default:
                levelNum = model.cookie.playerOfflineLevel;
                price = model.meta.getOfflineEarningCost();
                defaultCount = model.meta.getOfflineEarning();
        }

        // Check booster gate (TG only) — block when level reaches tier cap
        if (__SOCIAL__ === "tg") {
            const kind = boosterKindMap[boost.id];
            const tier = kind === "spawn" ? apiClient.spawnTier
                : kind === "startPop" ? apiClient.startPopTier
                : apiClient.offlineTier;
            if (isBoosterGated(levelNum, tier)) {
                isGated = true;
                apiClient.trackFunnelEvent("booster_gate_shown", { boosterType: kind, tier });
                gatePrice = getBoosterGatePrice(getBoosterMaxLevel(tier), apiClient.boosterGateBasePrice, apiClient.boosterGateMaxTier);
                onUnlock = async () => {
                    try {
                        const {paid} = await apiClient.purchaseFlow("unlock_" + kind);
                        if (paid) {
                            const refreshAfterPayment = async (retries = 3) => {
                                for (let i = 0; i < retries; i++) {
                                    if (i > 0) await new Promise(r => setTimeout(r, 1500));
                                    await apiClient.getMe();
                                    const newTier = kind === "spawn" ? apiClient.spawnTier
                                        : kind === "startPop" ? apiClient.startPopTier
                                        : apiClient.offlineTier;
                                    if (newTier > tier) break;
                                }
                                setState(state => ({
                                    ...state,
                                    coins: model.cookie.coins,
                                    tierVersion: state.tierVersion + 1
                                }));
                            };
                            refreshAfterPayment();
                        }
                    } catch (e) {
                        console.error("Booster unlock error:", e);
                    }
                };
            }
        }

        defaultCount = toFixedString(defaultCount);

        return {defaultCount, price, levelNum, disabled, isFree, isGated, gatePrice, onClick, onUnlock};
    };

    if (state.insufficientPopup) {
        const {deficit, pack} = state.insufficientPopup;
        const packCoins = (pack.payload as any)?.coins ?? 0;
        return (
            <InsufficientCoinsPopup
                deficit={deficit}
                packCoins={packCoins}
                packPrice={pack.price}
                packSlug={pack.slug}
                onClose={() => setState(s => ({...s, insufficientPopup: null}))}
                onPurchased={(coins) => {
                    model.cookie.coins += coins;
                    (window as any).__syncCoins?.();
                    setState(s => ({
                        ...s,
                        insufficientPopup: null,
                        coins: model.cookie.coins
                    }));
                }}
            />
        );
    }

    return (
        <div className={cn("boosters", className)}>
            {state.boosters
                .filter(i => i.allowed)
                .map((item: any) => (
                    <Booster {...item} />
                ))}
        </div>
    );
};
